import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocalShipping,
  FlightTakeoff,
  Inventory2,
  CheckCircle,
  ArrowBack,
  ContentCopy,
  LocationOn,
  CalendarToday,
  AccessTime,
  Person,
  Scale,
} from "@mui/icons-material";
import { deepPurple, green, orange, blue, grey, red } from "@mui/material/colors";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";
import { getOrderById } from "../../../Redux/Customers/Order/Action";

const PROGRESS_STEPS = [
  { label: "Picked Up", icon: Inventory2 },
  { label: "In Transit", icon: FlightTakeoff },
  { label: "Out for Delivery", icon: LocalShipping },
  { label: "Delivered", icon: CheckCircle },
];

function getActiveStep(statusType, scans) {
  if (statusType === "DL") return 4;
  if (!scans || scans.length === 0) return 0;

  const latestScanCode = scans[0]?.ScanCode?.trim();
  const latestScanType = scans[0]?.ScanType?.trim();

  if (latestScanType === "DL") return 4;
  if (latestScanCode === "002") return 3;
  if (latestScanType === "PU") return 1;
  return 2;
}

function getStatusColor(statusType) {
  switch (statusType) {
    case "DL":
      return green[600];
    case "UD":
      return blue[600];
    case "PU":
      return orange[700];
    default:
      return grey[600];
  }
}

function getStatusLabel(status, statusType) {
  if (statusType === "DL") return "Delivered";
  if (statusType === "PU") return "Pickup";
  if (status?.toLowerCase().includes("out for delivery")) return "Out for Delivery";
  if (status?.toLowerCase().includes("in transit") || status?.toLowerCase().includes("connected"))
    return "In Transit";
  return status || "Processing";
}

function getScanIcon(scanType, scanCode) {
  if (scanType === "DL") return <CheckCircle sx={{ fontSize: 20, color: green[500] }} />;
  if (scanType === "PU") return <Inventory2 sx={{ fontSize: 20, color: orange[600] }} />;
  if (scanCode === "002") return <LocalShipping sx={{ fontSize: 20, color: blue[500] }} />;
  return <LocationOn sx={{ fontSize: 20, color: deepPurple[400] }} />;
}

const TrackOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { order } = useSelector((store) => store);

  const [shipmentInfo, setShipmentInfo] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const awbNo = order.order?.awbNo ;

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderById(orderId));
    }
  }, [orderId, dispatch]);

  useEffect(() => {
    if (awbNo) {
      fetchTracking(awbNo);
    }
  }, [awbNo]);

  const fetchTracking = async (awb) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/track-order`, {
        params: { awbNo: awb },
      });

      const shipment = response.data.shipment;
      const shipmentData = Array.isArray(shipment) ? shipment[0] : shipment;

      if (!shipmentData) {
        setError("Shipment data not found.");
        return;
      }

      setShipmentInfo(shipmentData);

      const scanList = shipmentData?.Scans
        ? shipmentData.Scans.map((s) => s.ScanDetail)
        : [];

      setScans(scanList);
    } catch (err) {
      setError("Failed to fetch tracking information. Please try again later.");
      console.error("Tracking error:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAWB = () => {
    if (awbNo) {
      navigator.clipboard.writeText(awbNo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const activeStep = shipmentInfo
    ? getActiveStep(shipmentInfo.StatusType, scans)
    : 0;

  if (!order.order && !order.loading) {
    return (
      <Box className="flex flex-col items-center justify-center min-h-[60vh]">
        <Typography variant="h6" color="text.secondary">
          Order not found
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/account/order")}
          sx={{ mt: 2 }}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box className="px-4 lg:px-20 xl:px-36 py-6 min-h-screen" sx={{ bgcolor: grey[50] }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/account/order/${orderId}`)}
        sx={{ mb: 3, color: deepPurple[600], fontWeight: 600 }}
      >
        Back to Order Details
      </Button>

      {loading || order.loading ? (
        <Box className="flex flex-col items-center justify-center py-20">
          <CircularProgress sx={{ color: deepPurple[500] }} size={48} />
          <Typography sx={{ mt: 2, color: grey[600] }}>
            Fetching tracking information...
          </Typography>
        </Box>
      ) : error ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          {awbNo && (
            <Button
              variant="contained"
              onClick={() => fetchTracking(awbNo)}
              sx={{ mt: 2, bgcolor: deepPurple[500], "&:hover": { bgcolor: deepPurple[700] } }}
            >
              Retry
            </Button>
          )}
          {!awbNo && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              This order does not have a tracking number yet. Please check back later.
            </Typography>
          )}
        </Paper>
      ) : (
        <>
          {/* Shipment Summary Header */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 2.5, md: 4 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${deepPurple[600]} 0%, ${deepPurple[400]} 100%)`,
              mb: 3,
              overflow: "hidden",
              color: "white !important",
              "& *": { color: "white !important" },
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.5, fontWeight: 700 }}>
                  Tracking Shipment
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-all" }}>
                    AWB: {awbNo}
                  </Typography>
                  <Tooltip title={copied ? "Copied!" : "Copy AWB"}>
                    <IconButton
                      size="small"
                      onClick={handleCopyAWB}
                      sx={{ opacity: 0.8, "&:hover": { opacity: 1 } }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                  <Chip
                    label={shipmentInfo?.Status}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      height: "auto",
                      "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                    }}
                  />
                  {shipmentInfo?.ReceivedBy && shipmentInfo?.StatusType === "DL" && (
                    <Chip
                      icon={<Person sx={{ fontSize: 16 }} />}
                      label={`Received by: ${shipmentInfo.ReceivedBy}`}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        height: "auto",
                        "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                        "& .MuiChip-icon": { color: "white" },
                      }}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: { xs: 1, sm: 2 },
                    mt: { xs: 1, md: 0 },
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700 }}>
                      FROM
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      {shipmentInfo?.Origin}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                      {shipmentInfo?.OriginAreaCode}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: 40, sm: 60 },
                      height: 2,
                      bgcolor: "rgba(255,255,255,0.4)",
                      position: "relative",
                      mx: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        right: -4,
                        top: -3,
                        width: 0,
                        height: 0,
                        borderTop: "4px solid transparent",
                        borderBottom: "4px solid transparent",
                        borderLeft: "8px solid rgba(255,255,255,0.6)",
                      }}
                    />
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700 }}>
                      TO
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      {shipmentInfo?.Destination}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                      {shipmentInfo?.DestionationAreaCode}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Shipment Details Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                icon: <CalendarToday sx={{ color: deepPurple[500] }} />,
                label: "Pickup Date",
                value: shipmentInfo?.PickUpDate || "N/A",
              },
              {
                icon: <CalendarToday sx={{ color: green[600] }} />,
                label: "Expected Delivery",
                value: shipmentInfo?.ExpectedDelivery || "N/A",
              },
              {
                icon: <LocalShipping sx={{ color: blue[600] }} />,
                label: "Service",
                value: shipmentInfo?.Service || "N/A",
              },
              {
                icon: <Scale sx={{ color: orange[700] }} />,
                label: "Weight",
                value: shipmentInfo?.Weight ? `${shipmentInfo.Weight} kg` : "N/A",
              },
            ].map((card, idx) => (
              <Grid item xs={6} md={3} key={idx}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    height: "100%",
                  }}
                >
                  {card.icon}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, lineHeight: 1.3 }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Progress Stepper */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 3, color: deepPurple[700] }}
            >
              Shipment Progress
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                position: "relative",
                px: { xs: 0, md: 2 },
              }}
            >
              {/* Progress Line */}
              <Box
                sx={{
                  position: "absolute",
                  top: 24,
                  left: { xs: 24, md: 40 },
                  right: { xs: 24, md: 40 },
                  height: 4,
                  bgcolor: grey[200],
                  borderRadius: 2,
                  zIndex: 0,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 24,
                  left: { xs: 24, md: 40 },
                  width: `${Math.min(((activeStep - 1) / (PROGRESS_STEPS.length - 1)) * 100, 100)}%`,
                  height: 4,
                  bgcolor: activeStep > 0 ? deepPurple[500] : "transparent",
                  borderRadius: 2,
                  zIndex: 1,
                  transition: "width 0.8s ease-in-out",
                  maxWidth: `calc(100% - ${window.innerWidth < 600 ? 48 : 80}px)`,
                }}
              />

              {PROGRESS_STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const isCompleted = index + 1 <= activeStep;
                const isCurrent = index + 1 === activeStep;

                return (
                  <Box
                    key={step.label}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      position: "relative",
                      zIndex: 2,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isCompleted ? deepPurple[500] : grey[200],
                        boxShadow: isCurrent ? `0 0 0 4px ${deepPurple[100]}` : "none",
                        transition: "all 0.5s ease",
                      }}
                    >
                      <StepIcon sx={{ fontSize: 24, color: `${isCompleted ? "white" : grey[500]} !important` }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        fontWeight: isCompleted ? 700 : 400,
                        color: isCompleted ? deepPurple[700] : grey[500],
                        textAlign: "center",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>

          {/* Tracking Timeline */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 3, color: deepPurple[700] }}
            >
              Tracking Timeline
            </Typography>

            {scans.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No detailed scan information available.
              </Typography>
            ) : (
              <Box sx={{ position: "relative" }}>
                {scans.map((scan, index) => {
                  const isFirst = index === 0;
                  const isLast = index === scans.length - 1;
                  const isDelivered = scan.ScanType === "DL";
                  const location = scan.ScannedLocation?.replace(/^,\s*,\s*$/, "").trim();

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: { xs: 2, md: 3 },
                        position: "relative",
                        pb: isLast ? 0 : 3,
                      }}
                    >
                      {/* Timeline Connector */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          minWidth: 40,
                        }}
                      >
                        <Box
                          sx={{
                            width: isFirst ? 36 : 28,
                            height: isFirst ? 36 : 28,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: isFirst
                              ? isDelivered
                                ? green[50]
                                : deepPurple[50]
                              : grey[100],
                            border: `2px solid ${
                              isFirst
                                ? isDelivered
                                  ? green[500]
                                  : deepPurple[500]
                                : grey[300]
                            }`,
                            flexShrink: 0,
                          }}
                        >
                          {getScanIcon(scan.ScanType, scan.ScanCode)}
                        </Box>
                        {!isLast && (
                          <Box
                            sx={{
                              width: 2,
                              flexGrow: 1,
                              bgcolor: grey[300],
                              mt: 0.5,
                            }}
                          />
                        )}
                      </Box>

                      {/* Scan Content */}
                      <Box
                        sx={{
                          flex: 1,
                          pb: isLast ? 0 : 1,
                          bgcolor: isFirst ? (isDelivered ? green[50] : deepPurple[50]) : "transparent",
                          borderRadius: isFirst ? 2 : 0,
                          p: isFirst ? 2 : 0,
                          border: isFirst
                            ? `1px solid ${isDelivered ? green[200] : deepPurple[200]}`
                            : "none",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isFirst ? 700 : 600,
                            color: isFirst
                              ? isDelivered
                                ? green[800]
                                : deepPurple[800]
                              : grey[800],
                            fontSize: isFirst ? "0.95rem" : "0.85rem",
                          }}
                        >
                          {scan.Scan?.trim()}
                        </Typography>
                        {location && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <LocationOn
                              sx={{
                                fontSize: 14,
                                color: grey[500],
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: grey[600] }}
                            >
                              {location}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mt: 0.5,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <CalendarToday sx={{ fontSize: 12, color: grey[400] }} />
                            <Typography variant="caption" sx={{ color: grey[500] }}>
                              {scan.ScanDate}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <AccessTime sx={{ fontSize: 12, color: grey[400] }} />
                            <Typography variant="caption" sx={{ color: grey[500] }}>
                              {scan.ScanTime}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>

          {/* Sender/Receiver Info */}
          {(shipmentInfo?.SenderName || shipmentInfo?.ToAttention) && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mt: 3 }}>
              <Grid container spacing={3}>
                {shipmentInfo?.SenderName && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      SENDER
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {shipmentInfo.SenderName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {shipmentInfo.CustomerName}
                    </Typography>
                  </Grid>
                )}
                {shipmentInfo?.ToAttention && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      RECEIVER
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {shipmentInfo.ToAttention}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default TrackOrder;
